import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, ClockCountdown, XCircle, Prohibit, HourglassMedium, UsersThree, Clock } from "@phosphor-icons/react";

const STATUS_META = {
  PENDING_REVIEW: { label: "En attente", color: "#F59E0B", icon: HourglassMedium },
  APPROVED: { label: "Validée", color: "#00E5FF", icon: CheckCircle },
  WAITLISTED: { label: "Liste d'attente", color: "#8B5CF6", icon: ClockCountdown },
  REJECTED: { label: "Refusée", color: "#EF4444", icon: XCircle },
  SUSPENDED: { label: "Suspendue", color: "#EF4444", icon: Prohibit },
};

const ACTIONS = [
  { status: "APPROVED", label: "Approuver", cls: "bg-accent text-black" },
  { status: "WAITLISTED", label: "Liste d'attente", cls: "bg-surface-2 border border-border text-fg" },
  { status: "REJECTED", label: "Refuser", cls: "bg-surface-2 border border-border text-destructive" },
  { status: "SUSPENDED", label: "Suspendre", cls: "bg-surface-2 border border-border text-destructive" },
];

const FILTERS = ["PENDING_REVIEW", "APPROVED", "WAITLISTED", "REJECTED", "SUSPENDED", "ALL"];
const FILTER_LABEL = { PENDING_REVIEW: "En attente", APPROVED: "Validées", WAITLISTED: "Liste d'attente", REJECTED: "Refusées", SUSPENDED: "Suspendues", ALL: "Toutes" };

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("PENDING_REVIEW");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/organizations");
      setOrgs(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (org, status) => {
    setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, approval_status: status } : o)));
    try {
      await api.patch(`/admin/organizations/${org.id}/status`, { status });
      toast.success(`${org.name} — ${STATUS_META[status].label}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  const blacklistOrg = async (org) => {
    if (!window.confirm(`Refuser ${org.name} et blacklister ses membres ? Ils ne pourront plus se réinscrire avec ces emails.`)) return;
    try {
      const { data } = await api.post(`/admin/organizations/${org.id}/blacklist`);
      toast.success(`${org.name} refusée · ${data.members_disabled} compte(s) blacklisté(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const extendTrial = async (org) => {
    try {
      const { data } = await api.post(`/admin/organizations/${org.id}/extend-trial`, { days: 7 });
      const end = data.trial_ends_at ? new Date(data.trial_ends_at).toLocaleDateString("fr-FR") : "";
      toast.success(`${org.name} — essai prolongé de 7 jours (jusqu'au ${end})`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "ALL" ? orgs.length : orgs.filter((o) => o.approval_status === f).length;
    return acc;
  }, {});
  const shown = filter === "ALL" ? orgs : orgs.filter((o) => o.approval_status === filter);

  return (
    <div data-testid="admin-organizations">
      <div className="overline mb-2">Validation des accès</div>
      <h1 className="font-display text-3xl font-bold mb-6">Organisations (ESN)</h1>

      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList className="bg-surface border border-border flex-wrap h-auto">
          {FILTERS.map((f) => (
            <TabsTrigger key={f} value={f} data-testid={`org-filter-${f}`}>
              {FILTER_LABEL[f]} <span className="ml-1.5 font-mono text-xs opacity-70">{counts[f]}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : shown.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center text-muted" data-testid="org-empty">Aucune organisation dans cette catégorie.</div>
      ) : (
        <div className="space-y-3" data-testid="org-list">
          {shown.map((o) => {
            const meta = STATUS_META[o.approval_status] || STATUS_META.PENDING_REVIEW;
            const Icon = meta.icon;
            return (
              <div key={o.id} className="surface rounded-lg p-5" data-testid={`org-row-${o.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-fg">{o.name}</h3>
                      <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full border" style={{ color: meta.color, borderColor: meta.color + "55", background: meta.color + "14" }}>
                        <Icon size={11} weight="fill" /> {meta.label}
                      </span>
                      {o.plan && <span className="text-xs font-mono text-accent border border-accent/30 rounded-full px-2 py-0.5">{o.plan}</span>}
                    </div>
                    <div className="text-sm text-muted mt-1">{o.owner_name} · <span className="font-mono">{o.owner_email}</span></div>
                    <div className="flex items-center gap-3 text-xs text-muted mt-1.5">
                      <span className="flex items-center gap-1"><UsersThree size={13} /> {o.member_count} membre{o.member_count > 1 ? "s" : ""}</span>
                      {o.subscription_status && o.subscription_status !== "none" && <span className="font-mono">{o.subscription_status}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {ACTIONS.filter((a) => a.status !== o.approval_status).map((a) => (
                      <button key={a.status} onClick={() => setStatus(o, a.status)} data-testid={`org-action-${a.status}-${o.id}`}
                        className={`text-xs font-semibold px-3 py-2 rounded-md hover-lift ${a.cls}`}>
                        {a.label}
                      </button>
                    ))}
                    <button onClick={() => blacklistOrg(o)} data-testid={`org-blacklist-${o.id}`}
                      className="text-xs font-semibold px-3 py-2 rounded-md hover-lift bg-surface-2 border border-red-400/40 text-red-400 flex items-center gap-1">
                      <Prohibit size={13} weight="fill" /> Refuser + blacklister
                    </button>
                    <button onClick={() => extendTrial(o)} data-testid={`org-extend-trial-${o.id}`}
                      disabled={o.trial_extended_count >= 1}
                      title={o.trial_extended_count >= 1 ? "Essai déjà prolongé une fois" : "Prolonger l'essai de cette organisation de 7 jours"}
                      className="text-xs font-semibold px-3 py-2 rounded-md hover-lift bg-surface-2 border border-accent/40 text-accent flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                      <Clock size={13} weight="fill" /> {o.trial_extended_count >= 1 ? "Essai déjà prolongé" : "Prolonger l'essai +7j"}
                    </button>
                  </div>
                </div>
                {/* Fiche facturation / quotas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-4 pt-3 border-t border-border/50 text-xs" data-testid={`org-billing-${o.id}`}>
                  <div><span className="text-muted block">Offre effective</span><span className="font-mono text-fg">{o.effective_plan || "—"}</span></div>
                  <div><span className="text-muted block">Statut abonnement</span><span className="font-mono text-fg">{o.subscription_status || "—"}</span></div>
                  <div><span className="text-muted block">Utilisateurs</span><span className="font-mono text-fg">{o.member_count}/{o.max_users ?? "—"}</span></div>
                  <div><span className="text-muted block">Consultants actifs</span><span className="font-mono text-fg">{o.consultants_used ?? 0}/{o.max_consultants ?? "—"}</span></div>
                  <div><span className="text-muted block">Contacts ({o.contact_quota_window === "month" ? "ce mois" : "total"})</span><span className="font-mono text-fg">{o.contacts_used ?? 0}/{o.contact_quota ?? "—"}</span></div>
                  <div><span className="text-muted block">Fin d'essai</span><span className="font-mono text-fg">{o.trial_ends_at ? new Date(o.trial_ends_at).toLocaleDateString("fr-FR") : "—"}</span></div>
                  <div><span className="text-muted block">Prolongation 7j</span><span className="font-mono text-fg">{o.trial_extended_count >= 1 ? `utilisée${o.trial_extended_at ? " · " + new Date(o.trial_extended_at).toLocaleDateString("fr-FR") : ""}` : "non"}</span></div>
                  <div><span className="text-muted block">{o.plan === "mission_express" ? "Expiration Express" : "Prochaine échéance"}</span><span className="font-mono text-fg">{o.paid_until ? new Date(o.paid_until).toLocaleDateString("fr-FR") : "—"}</span></div>
                  {o.plan_started_at && <div><span className="text-muted block">Début du plan</span><span className="font-mono text-fg">{new Date(o.plan_started_at).toLocaleDateString("fr-FR")}</span></div>}
                  {o.stripe_customer_id && <div className="col-span-2"><span className="text-muted block">Stripe</span><span className="font-mono text-fg/70 truncate">{o.stripe_customer_id}</span></div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
